import React, { useCallback } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { InferType } from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { Button, ButtonVariant } from '../../../common/Button'
import { useAppTranslation } from '../../../../hooks/useAppTranslation'
import { TextInput } from '../../../common/TextInput'
import { useFormErrorMessage } from '../../../../hooks/useFormErrorMessage'
import { FormControl } from '../../../common/FormControl'
import { societyEntranceCreateSchema } from '../../../../forms/schemas/society_entrance_create'
import { ISocietyEntranceModel } from '../../../../interfaces/models/society-entrance.interfaces'
import { ISocietiesModel } from '../../../../interfaces/models/societies.interfaces'
import { i18nSocietyType } from '../../../../i18n'

interface CreateUpdateRegisterEntranceItemFormProps {
  onError: () => void
  onSubmit: (
    data: InferType<typeof societyEntranceCreateSchema>
  ) => Promise<void>
  onClose: () => void
  loading: boolean
  entrance?: ISocietyEntranceModel
  society: ISocietiesModel
}

export const CreateUpdateRegisterEntranceItemForm = ({
  entrance,
  loading,
  onError,
  onSubmit,
  onClose,
  society,
}: CreateUpdateRegisterEntranceItemFormProps): React.ReactNode => {
  const { translate } = useAppTranslation()
  const { getErrorMessage } = useFormErrorMessage()

  const updateMode = !!entrance

  const getDefaultValues = useCallback((): InferType<
    typeof societyEntranceCreateSchema
  > => {
    return {
      addressStreet: updateMode ? entrance?.addressStreet || '' : '',
      societyId: updateMode ? entrance?.societyId || society._id : society._id,
    }
  }, [society, entrance, updateMode])

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm({
    mode: 'all',
    resolver: yupResolver(societyEntranceCreateSchema),
    defaultValues: getDefaultValues(),
  })

  return (
    <>
      <Controller
        control={control}
        name="addressStreet"
        render={({ field: { value, name, onChange, onBlur } }) => (
          <FormControl
            label={translate('registerEntrance.form.labels.addressStreet', {
              context: i18nSocietyType(society?.societyTypeDisplayName),
            })}
            error={
              errors.addressStreet && getErrorMessage(errors.addressStreet)
            }
            name={name}
          >
            <TextInput
              value={value}
              onChange={onChange}
              onBlur={onBlur}
              error={!!errors.addressStreet}
            />
          </FormControl>
        )}
      />

      <div className="flex flex-shrink-0 flex-wrap items-center justify-end gap-4">
        <Button label={translate('common.actions.cancel')} onClick={onClose} />
        <Button
          disabled={!isValid || loading}
          variant={ButtonVariant.PRIMARY}
          label={
            updateMode
              ? translate('common.actions.save')
              : translate('common.actions.create')
          }
          type="submit"
          onClick={handleSubmit(onSubmit, onError)}
        />
      </div>
    </>
  )
}
